// Bakes a JPEG's EXIF orientation into its actual pixels (and downscales
// oversized photos) before upload.
//
// Why this is needed: a photo shot in portrait on an iPhone is stored as
// LANDSCAPE pixels plus an EXIF Orientation tag (usually 6 = rotate 90° CW)
// telling the viewer to turn it. Two things then went wrong:
//
//   1. The upload preview and the saved avatar both showed the photo on its
//      side - the orientation tag was never applied to the pixels, and the
//      backend's own image processing drops EXIF, so nothing downstream could
//      recover it either.
//   2. FormImageUpload measures `img.width/height` to feed PreImage's
//      `paddingTop: (height/width)*100` aspect-ratio box. Those are the RAW
//      (un-rotated) dimensions, so a portrait photo reserved a landscape box.
//
// Baking the rotation in fixes both at once, and the downscale is a real win
// on phones besides: a 12MP iPhone shot is 3-5 MB, and shrinking it to a
// 1600px avatar makes each upload finish in a fraction of the time - which
// matters when adding photos to many players in a row.
//
// Double-rotation is the trap here: some browsers already apply EXIF
// orientation when the image is decoded (so `drawImage` yields corrected
// pixels), others don't, and it varies by version. Rather than guess per
// browser, detectAutoOriented() below works it out from the file in hand -
// it compares the decoded dimensions against the true encoded dimensions read
// straight out of the JPEG's SOF marker. No user-agent sniffing, no test
// fixture.

const JPEG_TYPES = ["image/jpeg", "image/jpg"];

// EXIF Orientation values that swap width and height (the 90° rotations).
const SWAPS_AXES = [5, 6, 7, 8];

// Reads the EXIF Orientation tag (0x0112) out of a JPEG's APP1 segment.
// Returns 1 (= "as-is") for anything it can't read, so an unparseable or
// EXIF-less file just passes through untransformed.
const readExifOrientation = (view) => {
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return 1; // not a JPEG (no SOI)

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
        const marker = view.getUint16(offset, false);
        if ((marker & 0xff00) !== 0xff00) break; // desynced - give up rather than misread
        const size = view.getUint16(offset + 2, false);

        if (marker === 0xffe1) { // APP1 - where EXIF lives
            const exifStart = offset + 4;
            // Every read below is bounds-checked up front: a truncated or
            // malformed APP1 segment otherwise makes DataView throw a
            // RangeError, and while the caller does catch it, that also
            // discards the (independent) SOF dimension read the caller needs.
            if (exifStart + 4 > view.byteLength) return 1;
            // "Exif\0\0"
            if (view.getUint32(exifStart, false) !== 0x45786966) return 1;

            const tiff = exifStart + 6;
            if (tiff + 8 > view.byteLength) return 1;
            const little = view.getUint16(tiff, false) === 0x4949; // "II" vs "MM"
            const ifdOffset = view.getUint32(tiff + 4, little);
            const ifd = tiff + ifdOffset;
            if (ifd < tiff || ifd + 2 > view.byteLength) return 1;

            const entries = view.getUint16(ifd, little);
            for (let i = 0; i < entries; i++) {
                const entry = ifd + 2 + i * 12;
                if (entry + 12 > view.byteLength) break;
                if (view.getUint16(entry, little) === 0x0112) {
                    const value = view.getUint16(entry + 8, little);
                    return value >= 1 && value <= 8 ? value : 1;
                }
            }
            return 1;
        }

        if (marker === 0xffda) break; // start of scan - no EXIF before the image data
        if (size < 2) break;
        offset += 2 + size;
    }
    return 1;
};

// The encoded (pre-rotation) dimensions, read from whichever SOFn frame header
// the file uses. This is the ground truth we compare the browser's decode
// against, so we can tell whether it auto-applied the orientation.
const readJpegSize = (view) => {
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return null;

    let offset = 2;
    while (offset + 9 <= view.byteLength) {
        const marker = view.getUint16(offset, false);
        if ((marker & 0xff00) !== 0xff00) break;
        const low = marker & 0xff;
        // SOF0..SOF15, minus DHT (0xC4), JPGA (0xC8) and DAC (0xCC), which
        // share the 0xCn range but aren't frame headers.
        const isSOF = low >= 0xc0 && low <= 0xcf && low !== 0xc4 && low !== 0xc8 && low !== 0xcc;
        if (isSOF) {
            return { width: view.getUint16(offset + 7, false), height: view.getUint16(offset + 5, false) };
        }
        if (marker === 0xffda) break;
        const size = view.getUint16(offset + 2, false);
        if (size < 2) break;
        offset += 2 + size;
    }
    return null;
};

// true when the browser already rotated the pixels for us during decode, in
// which case applying the EXIF transform again would rotate it twice.
const detectAutoOriented = (orientation, encoded, decodedW, decodedH) => {
    if (!SWAPS_AXES.includes(orientation) || !encoded) return false;
    // Axes swapped relative to the encoded frame => the decode honoured EXIF.
    return decodedW === encoded.height && decodedH === encoded.width;
};

// Blob.arrayBuffer() only landed in Safari 14, and this project's browserslist
// still targets ios >= 12 - fall back to FileReader there rather than throwing
// (which would skip the orientation fix on exactly the phones that need it).
const readArrayBuffer = (blob) => {
    if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("read failed"));
        reader.readAsArrayBuffer(blob);
    });
};

const loadImage = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("decode failed"));
        img.src = url;
    });

const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => {
        if (canvas.toBlob) canvas.toBlob(resolve, type, quality);
        else resolve(null); // ancient browser - caller falls back to the original file
    });

// Maps an EXIF orientation onto a canvas transform. `w`/`h` are the
// dimensions the source is drawn at (i.e. post-scale, pre-rotation).
const applyOrientation = (ctx, orientation, w, h) => {
    switch (orientation) {
        case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;  // flip horizontal
        case 3: ctx.transform(-1, 0, 0, -1, w, h); break; // 180°
        case 4: ctx.transform(1, 0, 0, -1, 0, h); break;  // flip vertical
        case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;   // transpose
        case 6: ctx.transform(0, 1, -1, 0, h, 0); break;  // 90° CW
        case 7: ctx.transform(0, -1, -1, 0, h, w); break; // transverse
        case 8: ctx.transform(0, -1, 1, 0, 0, w); break;  // 90° CCW
        default: break;
    }
};

/**
 * Returns { file, width, height } ready to upload and to size a preview box
 * with. Never throws: on any failure it hands back the original file with the
 * best dimensions it managed to determine, so a bad parse degrades to today's
 * behaviour instead of blocking the upload.
 */
export const normalizeImageFile = async (file, { maxSize = 1600, quality = 0.85 } = {}) => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const isJpeg = JPEG_TYPES.includes(file.type) || /\.jpe?g$/i.test(file.name || "");

        let orientation = 1;
        let encoded = null;
        if (isJpeg) {
            try {
                const view = new DataView(await readArrayBuffer(file));
                orientation = readExifOrientation(view);
                encoded = readJpegSize(view);
            } catch (e) {
                // Unreadable header - fall through with orientation 1.
            }
        }

        const img = await loadImage(objectUrl);
        const decodedW = img.naturalWidth || img.width;
        const decodedH = img.naturalHeight || img.height;

        if (detectAutoOriented(orientation, encoded, decodedW, decodedH)) {
            orientation = 1; // browser already did it
        }

        const scale = Math.min(1, maxSize / Math.max(decodedW, decodedH));
        const drawW = Math.max(1, Math.round(decodedW * scale));
        const drawH = Math.max(1, Math.round(decodedH * scale));
        const swap = SWAPS_AXES.includes(orientation);

        // Nothing to correct and nothing to shrink - leave the bytes alone
        // (notably keeps PNG transparency intact instead of flattening it).
        if (orientation === 1 && scale === 1) {
            return { file, width: decodedW, height: decodedH };
        }

        const canvas = document.createElement("canvas");
        canvas.width = swap ? drawH : drawW;
        canvas.height = swap ? drawW : drawH;
        const ctx = canvas.getContext("2d");
        if (!ctx) return { file, width: decodedW, height: decodedH };

        applyOrientation(ctx, orientation, drawW, drawH);
        ctx.drawImage(img, 0, 0, drawW, drawH);

        // Re-encode as JPEG once we've touched the pixels, except for a PNG we
        // only resized - that one stays PNG so transparency survives.
        const outType = !isJpeg && orientation === 1 ? "image/png" : "image/jpeg";
        const blob = await canvasToBlob(canvas, outType, outType === "image/jpeg" ? quality : undefined);
        if (!blob) return { file, width: decodedW, height: decodedH };

        const ext = outType === "image/png" ? "png" : "jpg";
        const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
        const normalized = new File([blob], `${baseName}.${ext}`, {
            type: outType,
            lastModified: Date.now(),
        });

        return { file: normalized, width: canvas.width, height: canvas.height };
    } catch (e) {
        console.error("Image normalize failed, uploading original", e);
        return { file, width: null, height: null };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

export default normalizeImageFile;
